import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Avatar,
  Box,
  ThemeProvider,
  createTheme,
  Tabs,
  Tab,
  Typography,
  Chip,
} from "@mui/material";
import { useAuth } from "../utils/userAuth";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { getUrlWithoutHost } from "../utils/url";
import useAutoRefresh from "./useAutoRefresh";
import { useLogPageLoad } from "./useLogPageLoad";
import { SnackbarProvider, useSnackbar } from "./SnackbarProvider";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, AppState } from "../store";
import { fetchEnvInfo, selectEnvInfo } from "../store/systemData";
import { baseDobbyTheme } from "../theme/dobbyTheme";
import FeedbackButton from "./FeedbackButton";

export interface MenuItem {
  label: string;
  value: string;
  icon?: React.ReactElement;
  onClick?: () => void;
  href?: string;
}

// Re-export for backward compatibility
export { useSnackbar };

interface DobbyGenericPageProps {
  children: React.ReactNode;
  fullHeight?: boolean;
  style?: React.CSSProperties;
  sx?: object;
  title?: string;
  menus?: MenuItem[];
  currentMenuValue?: string;
}

const DobbyGenericPage: React.FC<DobbyGenericPageProps> = ({
  children,
  fullHeight = false,
  style,
  sx,
  title,
  currentMenuValue,
  menus,
}) => {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const envInfo = useSelector(selectEnvInfo);
  const user = useSelector((state: AppState) => state.userData.user);

  // Auto-refresh page
  useAutoRefresh(
    1000 * 60 * 60 * 3 /* 3 hours */,
    1000 * 60 * 5 /* 5 minutes */
  );

  // Log page views to telemetry
  useLogPageLoad();

  // Fetch environment info if not already loaded
  useEffect(() => {
    if (!envInfo) {
      dispatch(fetchEnvInfo());
    }
  }, [dispatch, envInfo]);

  if (!isLoggedIn) {
    const redirectUrl = getUrlWithoutHost();
    return <Navigate to={`/user/login?redirectUrl=${redirectUrl}`} replace />;
  }

  return (
    <ThemeProvider theme={baseDobbyTheme}>
      {/* <SnackbarProvider> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: fullHeight ? "100vh" : "auto",
          ...sx,
        }}
        style={style}
      >
        <AppBar
          sx={{
            backgroundColor: envInfo?.isProd ? "#000000" : undefined,
          }}
          position="static"
          color="primary"
        >
          <Toolbar>
            <Avatar
              src="/www/assets/dobby_internal_tools_icon.png"
              alt="Dobby Tools Icon"
              sx={{ marginRight: 1, width: 34, height: 34 }}
            />
            {title && (
              <Box sx={{ color: "white", fontSize: 16, fontWeight: 500 }}>
                {title}
              </Box>
            )}
            {user?.isAdmin && (
              <Chip
                label="Admin"
                size="small"
                onClick={() => navigate("/admin")}
                sx={{
                  ml: 1,
                  backgroundColor: "#fedf33",
                  color: "#000",
                  fontWeight: 500,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#ffe766",
                  },
                }}
              />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Tabs
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#fedf33",
                  height: "4px",
                },
                "& .MuiTab-root": {
                  minHeight: "28px",
                  fontSize: "0.75rem",
                  padding: "10px 8px",
                },
              }}
              value={currentMenuValue}
              textColor="inherit"
              aria-label="navigation tabs"
            >
              {menus &&
                menus.map((menu, index) => (
                  <Tab
                    key={menu.label}
                    icon={menu.icon as any}
                    label={menu.label}
                    iconPosition="start"
                    value={menu.value}
                    component={Link}
                    to={menu.href as any}
                    onClick={menu.onClick}
                  />
                ))}
            </Tabs>
          </Toolbar>
        </AppBar>
        <Box
          sx={{
            p: 0,
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            height: fullHeight ? "calc(100vh - 40px)" : "auto",
          }}
        >
          {children}
        </Box>

        {/* Floating Feedback Button */}
        <FeedbackButton />
      </Box>
      {/* </SnackbarProvider> */}
    </ThemeProvider>
  );
};

export default DobbyGenericPage;
